<template>
  <div class="driver-datatable">
    <v-data-table
      :headers="headers"
      :items="driverData.trips"
      :items-per-page="5"
      :mobile-breakpoint="0"
      item-key="first_event_time"
      :options.sync="driverData.options"
      :loading="tableLoading"
      :server-items-length="driverData.totalTrips"
      multi-sort
      :footer-props="footerProps"
      :calculateWidths="true"
      class="elevation-1">

      <!-- header slots -->
      <template v-for="item in iconedHeaders" v-slot:[`header.${item}`]="{header}">
              <span :key="item" class="d-inline-flex align-center justify-start "
                    style="vertical-align: middle; word-wrap: break-word">
                <v-tooltip top v-if="header.hasOwnProperty('icon')">
                  <template v-slot:activator="{ on }">
                    <v-icon left class="pointer" v-on="on">{{ header.icon }}</v-icon>
                  </template>
                  <span v-html="header.text"></span>
                </v-tooltip>
              </span>
      </template>

      <!-- filters -->
      <template v-slot:body.prepend="{ headers }">
        <tr>
          <td v-for="header in headers" :key="header.value" :colspan="1">
            <template v-if="filters.hasOwnProperty(header.value)">
              <range-filter
                v-if="filters[header.value].type === 'range'"
                :column="header.value"
                :disabled="getRangeFilterBound('max', header.value) === 0"
                :header="header.text"
                :max="getRangeFilterBound('max', header.value)"
                :min="getRangeFilterBound('min', header.value)"
                :bounds-tracking="true"
                @value-changed="filterColumn">
              </range-filter>

              <datetime-filter
                v-if="filters[header.value].type === 'datetime'"
                :column="header.value"
                :header="filters[header.value].header"
                :min="getDatetimeFilterBound('min', header.value)"
                :max="getDatetimeFilterBound('max', header.value)"
                @datetime-changed="datetimeUpdate"
                @datetime-cleared="datetimeCleared">
              </datetime-filter>

              <duration-group-filter
                v-if="filters[header.value].type === 'duration'"
                :header="filters[header.value].header"
                :column="header.value"
                @duration-changed="durationUpdate"
                @duration-cleared="durationCleared"
              >
              </duration-group-filter>
            </template>
          </td>
        </tr>
      </template>

      <template v-slot:item.states="{item}">
        <v-tooltip top class="align-center" v-if="item.states > 0 || item.states === -1">
          <template v-slot:activator="{ on }">
            <v-icon left v-on="on">
              {{ item.states > 0 ? stateUncheckedIcon : stateReportOffIcon }}
            </v-icon>
          </template>
          <span>{{
              item.states > 0 ? $vuetify.lang.t(`$vuetify.map.managerMap.uncheckedStates`, item.states) : $vuetify.lang.t(`$vuetify.map.managerMap.reportOff`)
            }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.requests="{item}">
        <template v-for="state of Object.values(ClientFileStatus)">
          <tooltip-basic :key="`requests-${item.id}-${state}`"
                         v-if="state in item.requests"
                         :text="`$vuetify.map.requests.dataIterator.statesNumbered.${state}`"
                         :text-parameter="item.requests[state]"
                         :icon="reportsIcons[state]"
          ></tooltip-basic>
        </template>
      </template>

      <template v-slot:item.distance="{item}">
        {{ distanceLocalized(item.distance / 1000, false) }}
      </template>
      <template v-slot:item.first_event_time="{item}">
        {{ travelDateLocalized(item.first_event_time) }}
      </template>
      <template v-slot:item.duration="{item}">
        {{ durationLocalized(item.date_end - item.first_event_time) }}
      </template>
      <template v-slot:item.average_speed="{item}">
        {{ valueFormatted(item.average_speed) }}
      </template>
      <template v-slot:item.max_speed="{item}">
        {{ valueFormatted(item.max_speed) }}
      </template>
      <template v-slot:item.max_acceleration="{item}">
        {{ valueFormatted(item.max_acceleration) }}
      </template>

      <template v-slot:item.action="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn icon @click="tripRequest(item.date_start)" v-on="on">
              <v-icon>
                {{ mapIcon }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.t('$vuetify.map.driverTrips.actions.view') }}</span>
        </v-tooltip>

        <trip-download :critical-events-count="item.drowsiness_count + item.distraction_count"
                       :is-open="driverData.downloadDialog" :date-start="item.date_start"
                       :user-id="driverId"></trip-download>

        <video-download v-if="item.video_count" :user-id="driverId" :travel-start="item.date_start"
                        :video-count="item.video_count"></video-download>

      </template>
    </v-data-table>
  </div>

</template>

<script>
import Vuetify from '@/plugins/vuetify'
import backend from '@/backend'
import clone from 'lodash.clonedeep'

import { distanceLocalized, durationLocalized, travelDateLocalized, valueFormatted } from '@/utils/travels'
import {
  mdiAlarm,
  mdiAxisArrow, mdiClipboardListOutline,
  mdiClockIn, mdiFileVideo, mdiMapMarkerDistance,
  mdiMapSearch,
  mdiRocket, mdiSpeedometer, mdiVideoCheck, mdiVideoOff
} from '@mdi/js'

import { AUTH_MODULE, DRIVER_TABLE_MODULE, SETTINGS_MODULE } from '@/store/modules'
import { createHelpers } from 'vuex-map-fields'
import RangeFilter from '../tableFilters/rangeFilter'
import TripDownload from './tripDownload'

import { defaultDatetimeFilterState, defaultDurationFilterState, defaultRangeFilterState } from '@/utils/filters'
import DatetimeFilter from '../tableFilters/datetimeFilter'
import { dateToCalendarEntry } from '@/utils/calendar'
import DurationGroupFilter from '../tableFilters/durationGroupFilter'
import VideoDownload from './videoDownload'
import snackbarMixin from '../../../mixins/snackbarMixin'
import { defineTableCustomTextHeader, defineTableHeader } from '@/utils/tableManager'
import { ClientFileStatus, DangerousState } from '@/enum'
import { dangerousStateIcons, requestsFilesIcons } from '@/utils/icons'
import TooltipBasic from '@/components/utils/tooltipBasic'
import { getCurrentTimezoneForBackend } from '@/utils/timezone'

const icons = dangerousStateIcons()

const { mapFields } = createHelpers({
  getterType: `${DRIVER_TABLE_MODULE}/getField`,
  mutationType: `${DRIVER_TABLE_MODULE}/updateField`
})

const { mapFields: authFields } = createHelpers({
  getterType: `${AUTH_MODULE}/getField`
})

const { mapFields: settingsFields } = createHelpers({
  getterType: `${SETTINGS_MODULE}/getField`
})

const getInnerColumn = (field) => {
  return field !== 'latest_trip' ? field : 'first_event_time'
}

const getOuterColumn = (field) => {
  return field !== 'first_event_time' ? field : 'latest_trip'
}

const isRangeBoundsExists = (bounds) => {
  return bounds.reduce((a, b) => a + b, 0) > 0 && bounds[1] > 0
}

const isDatetimeBoundsExists = (bounds) => {
  return bounds.start.date !== '' || bounds.finish.date !== ''
}

const isDurationBoundsExists = (bounds) => {
  return Number(bounds.seconds) + Number(bounds.minutes) + Number(bounds.hours) + Number(bounds.days) > 0
}

const mergeFilters = (outerFilters, innerFilters) => {
  const result = clone(innerFilters) // Object.assign({}, innerFilters) // copy inner filters
  for (const [field, filter] of Object.entries(outerFilters)) {
    const column = getInnerColumn(field)
    if (result.hasOwnProperty(column)) {
      // need to adjust inner filter
      // TODO: add here other type of filters ?
      if (filter.type === 'range' && isRangeBoundsExists(filter.value)) {
        // console.log(column, result[column].value[0], filter.value[0], result[column].value[0], filter.value[1])
        if (!(result[column].value[0] >= filter.value[0] && result[column].value[0] <= filter.value[1])) {
          result[column].value[0] = parseFloat(filter.value[0])
        }

        // console.log(column, result[column].value[1], filter.value[0], result[column].value[1], filter.value[1])
        if (!(result[column].value[1] >= filter.value[0] && result[column].value[1] <= filter.value[1])) {
          result[column].value[1] = parseFloat(filter.value[1])
        }
      }

      if (filter.type === 'datetime' && isDatetimeBoundsExists(filter.value)) {
        const filterBounds = [filter.value.start.date, filter.value.finish.date].sort()
        if (!(result[column].value.start.date >= filterBounds[0] && result[column].value.start.date <= filterBounds[1]) || !result[column].value.start.date) {
          result[column].value.start.date = filter.value.start.date
        }

        if (!(result[column].value.finish.date >= filterBounds[0] && result[column].value.finish.date <= filterBounds[1]) || !result[column].value.finish.date) {
          result[column].value.finish.date = filter.value.finish.date
        }
      }
    } else {
      result[column] = filter
    }
  }

  // console.log('mergeResult', result)

  return result
}

const defaultFilterState = () => ({
  // duration: {
  //   type: 'range',
  //   value: [0, 0], // default values - all data will be visible, will be updated later after server call
  //   maxServerValue: 0 // max count - will be fetched from server
  // },
  // 'date_start': {
  //   type: 'datetime',
  //   value: {
  //     start: defaultDatetimeFilterState(),
  //     finish: defaultDatetimeFilterState()
  //   },
  //   header: Vuetify.framework.lang.t('$vuetify.map.managerMap.filterHeaders.latestTrip'),
  //   minServerValue: '', // will be fetched from server
  //   maxServerValue: '' // will be fetched from server
  // },
  duration: {
    type: 'duration',
    value: {
      start: defaultDurationFilterState(),
      finish: defaultDurationFilterState()
    },
    header: Vuetify.framework.lang.t('$vuetify.map.managerMap.filterHeaders.duration')
  },
  distance: {
    type: 'range',
    value: [0, 0], // default values - all data will be visible, will be updated later after server call
    minServerValue: 0,
    maxServerValue: 0, // max count - will be fetched from server
    multiplier: 1000
  },
  'average_speed': defaultRangeFilterState(),
  'max_speed': defaultRangeFilterState(),
  'max_acceleration': defaultRangeFilterState(),
  // 'distraction_count': defaultRangeFilterState(),
  'distraction_no_attention': defaultRangeFilterState(),
  'distraction_no_face': defaultRangeFilterState(),
  'drowsiness_count': defaultRangeFilterState(),
  'nobelt_count': defaultRangeFilterState(),
  'camera_sabotage': defaultRangeFilterState(),
  'cellphone_use': defaultRangeFilterState(),
  'eating_count': defaultRangeFilterState(),
  'smoking_count': defaultRangeFilterState()
})

const getHeaders = (isManagerRestricted) => {
  const result = [
    defineTableHeader('$vuetify.map.travelStats.actions.title', 'action', mdiClipboardListOutline, false),
    defineTableHeader('', 'requests', null, false, 'left', { 'width': '2%' }),
    defineTableHeader('', 'states', null, false),
    defineTableHeader('$vuetify.map.travelStats.dateStart.title', 'first_event_time', mdiClockIn),
    defineTableHeader('$vuetify.map.travelStats.duration.title', 'duration', mdiAlarm),
    defineTableCustomTextHeader(`${Vuetify.framework.lang.t('$vuetify.map.travelStats.distance.title')} (${Vuetify.framework.lang.t('$vuetify.map.travelStats.distance.postfix')})`, 'distance', mdiMapMarkerDistance),
    defineTableCustomTextHeader(`${Vuetify.framework.lang.t('$vuetify.map.travelStats.averageSpeed.title')} (${Vuetify.framework.lang.t('$vuetify.map.travelStats.averageSpeed.postfix')})`, 'average_speed', mdiSpeedometer),
    defineTableCustomTextHeader(`${Vuetify.framework.lang.t('$vuetify.map.travelStats.maxSpeed.title')} (${Vuetify.framework.lang.t('$vuetify.map.travelStats.maxSpeed.postfix')})`, 'max_speed', mdiRocket),
    defineTableCustomTextHeader(`${Vuetify.framework.lang.t('$vuetify.map.travelStats.maxAcceleration.title')} (${Vuetify.framework.lang.t('$vuetify.map.travelStats.maxAcceleration.postfix')})`, 'max_acceleration', mdiAxisArrow)
  ]

  if (!isManagerRestricted) {
    result.push(...[
      defineTableHeader('$vuetify.map.managerMap.headers.distraction_no_attention', 'distraction_no_attention', icons[DangerousState.DISTRACTION_NO_ATTENTION]),
      defineTableHeader('$vuetify.map.managerMap.headers.distraction_no_face', 'distraction_no_face', icons[DangerousState.DISTRACTION_NO_FACE]),
      defineTableHeader('$vuetify.map.managerMap.headers.drowsiness', 'drowsiness_count', icons[DangerousState.DROWSINESS]),
      defineTableHeader('$vuetify.map.managerMap.headers.nobelt', 'nobelt_count', icons[DangerousState.BELT_NOT_FASTEN]),
      defineTableHeader('$vuetify.map.managerMap.headers.cameraSabotage', 'camera_sabotage', icons[DangerousState.CAMERA_SABOTAGE]),
      defineTableHeader('$vuetify.map.managerMap.headers.cellphoneUse', 'cellphone_use', icons[DangerousState.CELLPHONE_USE]),
      defineTableHeader('$vuetify.map.managerMap.headers.eatingCount', 'eating_count', icons[DangerousState.EATING]),
      defineTableHeader('$vuetify.map.managerMap.headers.smokingCount', 'smoking_count', icons[DangerousState.SMOKING])
    ])
  }

  return result
}

export default {
  name: 'driverTrips',
  components: { TooltipBasic, VideoDownload, DurationGroupFilter, DatetimeFilter, RangeFilter, TripDownload },
  mixins: [snackbarMixin],
  props: {
    driverId: Number,
    outerFilters: Object
  },
  data: () => ({
    expanded: [],
    tableLoading: true,
    firstLoad: true, // this var is needed because of max/min Server value initialization and properly handling
    footerProps: { 'items-per-page': 10, 'items-per-page-options': [5, 10, 25, 50, -1] }, // if add more - page overflow can be reached!
    mapIcon: mdiMapSearch,
    videoIcon: mdiFileVideo,
    stateUncheckedIcon: mdiVideoCheck,
    stateReportOffIcon: mdiVideoOff,
    reportsIcons: requestsFilesIcons,
    filters: defaultFilterState(),
    downloadDialogs: {}
  }),
  computed: {
    ...mapFields(['drivers']),
    ...authFields(['isSpecial', 'isAdmin', 'isManagerRestricted']),
    ...settingsFields(['mapSettings.hideSmallTrips', 'mapSettings.hideViewedTrips', 'mapLocalFilters.timeRangeFilter']),
    headers () {
      return getHeaders(this.isManagerRestricted)
    },
    iconedHeaders () {
      return this.headers.filter(header => header.hasOwnProperty('icon')).map(header => header.value)
    },
    driverData () {
      return this.drivers[this.driverId]
    }
  },
  methods: {
    async loadTrips (data = { includedTrip: null, page: null }) {
      this.tableLoading = true
      try {
        const conditions = {}

        if (this.hideSmallTrips) {
          conditions.hideShortTrips = true
        }

        if (this.hideViewedTrips) {
          conditions.hideViewedTrips = true
        }

        if (this.timeRangeFilter.isUsed) {
          conditions.timeRangeFilter = {
            ...this.timeRangeFilter.filter,
            timezone: getCurrentTimezoneForBackend()
          }
        }

        if (data.page) {
          this.firstLoad = true
          this.driverData.options.page = data.page
        }

        const result = await backend.fetchDriverTripList({
          driver_id: this.driverId,
          filters: mergeFilters(this.outerFilters, this.filters), // this.outerFilters, // mergeFilters(this.outerFilters, this.filters), // update data here
          ...this.driverData.options,
          conditions,
          includedTrip: data.includedTrip
        })

        if ('data' in result) {
          this.driverData.trips = result.data.items
          this.driverData.totalTrips = result.data.total

          const bounds = [
            { type: 'max_values', column: 'maxServerValue' },
            { type: 'min_values', column: 'minServerValue' }
          ]

          if (data.includedTrip && result.data.hasOwnProperty('page')) {
            this.driverData.options.page = result.data.page
          }

          // apply min/max filters
          // TODO: update data in one batch - in other case we just have cascade filtering on startup !!!
          for (const entry of Object.values(bounds)) {
            if (result.data.hasOwnProperty(entry.type)) {
              for (const [column, value] of Object.entries(result.data[entry.type])) {
                if (this.filters.hasOwnProperty(column)) {
                  let result = value
                  if (column === 'first_event_time') {
                    result = dateToCalendarEntry(new Date(value))
                  }

                  this.filters[column][entry.column] = result
                }
              }
            }
          }
        }
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.tableLoading = false
        if (this.firstLoad) {
          this.firstLoad = false
        }
      }
    },
    tripRequest (travelStart) {
      this.$emit('trip-request', { userId: this.driverId, travelStart })
    },

    // max or min values for range filters
    getRangeFilterBound (type, column) {
      const outerColumn = getOuterColumn(column)
      // console.log('getFilterBound', type, column)
      // if range is not [0, 0] - we can apply outer filter bounds
      if (this.outerFilters.hasOwnProperty(outerColumn) && isRangeBoundsExists(this.outerFilters[outerColumn].value)) {
        // console.log('getFilterBound', type, column, 'outer', JSON.stringify(this.outerFilters[outerColumn].value))
        const index = type === 'min' ? 0 : 1
        return parseFloat(this.outerFilters[outerColumn].value[index])
      } else {
        // console.log('getFilterBound', type, column, 'inner', JSON.stringify(this.filters[column]))
        return this.filters[column][`${type}ServerValue`]
      }
    },

    // min/max valuers for datetime filters
    getDatetimeFilterBound (type, column) {
      const outerColumn = getOuterColumn(column)
      if (this.outerFilters.hasOwnProperty(outerColumn) && isDatetimeBoundsExists(this.outerFilters[outerColumn].value)) {
        const index = type === 'min' ? 0 : 1
        const values = this.outerFilters[outerColumn].value
        return [values.start.date, values.finish.date].sort()[index]
      } else {
        return this.filters[column][`${type}ServerValue`]
      }
    },

    // filter functions
    async filterColumn (data) {
      const oldData = this.filters[data.column].value
      this.filters[data.column].value = data.value

      // fix for first empty update
      let update = true
      if (this.filters[data.column].type === 'range') {
        update = update && isRangeBoundsExists(oldData)
      }

      if (update) {
        await this.loadTrips() // update
      }
    },

    async datetimeUpdate (data) {
      this.filters[data.column].value[data.type][data.mode] = data.value

      const oldData = this.filters[data.column].value

      // fix for first empty update
      let update = true
      if (this.filters[data.column].type === 'datetime') {
        update = update && isDatetimeBoundsExists(oldData)
      }

      if (update) {
        await this.loadTrips() // update
      }
    },
    async datetimeCleared (data) {
      this.filters[data.column].value = {
        start: defaultDatetimeFilterState(),
        finish: defaultDatetimeFilterState()
      }

      await this.loadTrips() // update
    },

    async durationUpdate (data) {
      const oldData = this.filters[data.column].value
      this.filters[data.column].value[data.type] = Object.assign({}, data.values)

      // fix for first empty update
      let update = true
      if (this.filters[data.column].type === 'duration') {
        update = update && (isDurationBoundsExists(oldData.start) || isDurationBoundsExists(oldData.finish))
      }

      if (update) {
        await this.loadTrips() // update
      }

      // await this.loadTrips() // update
    },
    async durationCleared (data) {
      const fields = ['start', 'finish']
      for (const field of fields) {
        this.filters[data.column].value[field] = defaultDurationFilterState()
      }

      await this.loadTrips() // update
    }
  },
  watch: {
    'driverData.options': {
      async handler (value) {
        // we don't use mount for initial loading because first data loading occurs here
        if (!this.firstLoad) {
          await this.loadTrips()
        }
      },
      deep: true
    }
  },
  created () {
    this['distanceLocalized'] = distanceLocalized
    this['travelDateLocalized'] = travelDateLocalized
    this['durationLocalized'] = durationLocalized
    this['valueFormatted'] = valueFormatted
    this['ClientFileStatus'] = ClientFileStatus
  }
}
</script>

<style scoped>
>>> .driver-datatable .v-data-table__progress [role='progressbar'] {
  position: relative;
}
</style>
